import { useState, useEffect } from 'react';
import { analytics, logger } from '../services/analytics';
import { getAuthToken } from '../services/token-manager';

export interface PrivacyConsentState {
  hasConsent: boolean;
  isLoading: boolean;
  showBanner: boolean;
  setConsent: (consent: boolean) => void;
  dismissBanner: () => void;
}

export const usePrivacyConsent = (): PrivacyConsentState => {
  const [hasConsent, setHasConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Verificar consentimento existente
    const consent = analytics.checkUserConsent();
    setHasConsent(consent);

    if (!consent) {
      // Estratégia de exibição baseada no contexto do usuário
      const isLoggedIn = !!getAuthToken();
      const hasSeenBanner = localStorage.getItem('privacy_banner_seen');
      const lastSeen = localStorage.getItem('privacy_banner_last_seen');
      
      // Calcular delay baseado no contexto
      let delay = 0;
      
      if (!hasSeenBanner) {
        // Primeira visita: mostrar imediatamente
        delay = 0;
      } else if (lastSeen && shouldReShowBanner(lastSeen)) {
        // Re-exibir após 30 dias: delay baseado no status de login
        delay = isLoggedIn ? 3000 : 8000; // 3s para logado, 8s para não logado
      } else {
        // Não mostrar se já viu recentemente
        return;
      }

      setTimeout(() => {
        setShowBanner(true);
      }, delay);
    }
  }, []);

  const shouldReShowBanner = (lastSeen: string): boolean => {
    const lastSeenDate = new Date(lastSeen);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastSeenDate < thirtyDaysAgo;
  };

  const setConsent = async (consent: boolean) => {
    setIsLoading(true);
    try {
      analytics.setUserConsent(consent);
      setHasConsent(consent);
      
      // Registrar que o usuário viu o banner
      const now = new Date().toISOString();
      localStorage.setItem('privacy_banner_seen', 'true');
      localStorage.setItem('privacy_banner_last_seen', now);
      
      setShowBanner(false);
    } catch (error) {
      logger.error('Erro ao definir consentimento', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissBanner = () => {
    // Apenas esconder o banner sem definir consentimento
    const now = new Date().toISOString();
    localStorage.setItem('privacy_banner_seen', 'true');
    localStorage.setItem('privacy_banner_last_seen', now);
    setShowBanner(false);
  };

  return {
    hasConsent,
    isLoading,
    showBanner,
    setConsent,
    dismissBanner
  };
};
